// Frontend API client — thin fetch wrappers returning parsed JSON or throwing.

export type Role = "USER" | "OWNER" | "MODERATOR" | "ADMIN";
export type Locale = "en" | "fr" | "de";
export type UserGroup = "FAMILY" | "GUEST";

export type AuthUser = {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  role: Role;
  userGroup: UserGroup | null;
  validated: boolean;
  phone: string | null;
  preferredLang: Locale;
  active: boolean;
  familyId: string | null;
};

export type AdminUser = {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  role: Role;
  userGroup: UserGroup | null;
  validated: boolean;
  preferredLang: Locale;
  active: boolean;
  familyId: string | null;
  familyName: string | null;
  createdAt: string;
  _count?: { products: number; reservations: number };
};

export type FamilyMember = {
  id: string;
  name: string;
  username: string;
  role: Role;
  validated: boolean;
  userGroup: UserGroup | null;
};

export type FamilyGroup = {
  id: string;
  name: string;
  createdAt: string;
  members: FamilyMember[];
  products: Array<{ id: string; title: string }>;
};

export type ProductImage = {
  id: string;
  productId: string;
  url: string;
  caption: string | null;
  order: number;
};

export type Availability = {
  id: string;
  productId: string;
  startDate: string;
  endDate: string;
};

export type SeasonRange = {
  id: string;
  productId: string;
  name: string;
  startDate: string;
  endDate: string;
};

export type Product = {
  id: string;
  title: string;
  description: string;
  location: string;
  propertyType: string;
  bedrooms: number;
  bathrooms: number;
  maxGuests: number;
  pricePerNight: number;
  highSeasonRate: number;
  familyDiscountPercent: number;
  amenities: string;
  ownerId: string;
  familyId: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  images?: ProductImage[];
  availabilities?: Availability[];
  seasonRanges?: SeasonRange[];
  reservations?: {
    id: string;
    startDate: string;
    endDate: string;
    status: string;
    partySize: number;
    bookingType: string;
    userGroup: string | null;
  }[];
  owner?: { id: string; name: string; email: string };
};

export type Reservation = {
  id: string;
  productId: string;
  userId: string;
  startDate: string;
  endDate: string;
  status: "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED";
  bookingType: "PRIVATE" | "SHARED";
  partySize: number;
  userGroup: UserGroup | null;
  pricePerNight: number;
  guestName: string;
  guestEmail: string;
  guestPhone: string | null;
  notes: string | null;
  totalPrice: number;
  validatedBy: string | null;
  validatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  product?: Product & { images?: ProductImage[] };
  user?: { id: string; name: string; email: string };
  validator?: { id: string; name: string } | null;
  // Members sharing the stay (organiser first) with their equal split share.
  participants?: StayMember[];
};

export type StayMember = {
  id: string;
  userId: string | null;
  name: string;
  share: number;
  isOrganiser: boolean;
};

async function handle<T>(res: Response): Promise<T> {
  const text = await res.text();
  let data: any = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }
  if (!res.ok) {
    const message =
      (data && (data.error || data.message)) || `Request failed (${res.status})`;
    throw new Error(message);
  }
  return data as T;
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(path, { method: "GET", credentials: "include" });
  return handle<T>(res);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  });
  return handle<T>(res);
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "PATCH",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  });
  return handle<T>(res);
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "PUT",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  });
  return handle<T>(res);
}

export async function apiDelete<T>(path: string): Promise<T> {
  const res = await fetch(path, { method: "DELETE", credentials: "include" });
  return handle<T>(res);
}

export async function uploadFile(path: string, file: File): Promise<{ url: string }> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(path, { method: "POST", body: form, credentials: "include" });
  return handle<{ url: string }>(res);
}

// ---- Expenses ----
export type ExpenseType = "FIXED" | "PUNCTUAL";
export type ExpenseCategory =
  | "TAXES"
  | "ELECTRICITY"
  | "GAS"
  | "INTERNET"
  | "SUBSCRIPTION"
  | "MAINTENANCE"
  | "CUSTOM";
export type PaymentMethod = "CASH" | "BANK_TRANSFER" | "CARD" | "OTHER";

export type Expense = {
  id: string;
  productId: string;
  type: ExpenseType;
  category: ExpenseCategory;
  customLabel: string | null;
  description: string;
  amount: number;
  dueDate: string;
  periodStart: string | null;
  periodEnd: string | null;
  status: "PENDING" | "PAID";
  paidById: string | null;
  paidBy: { id: string; name: string } | null;
  paidDate: string | null;
  paymentMethod: PaymentMethod | null;
  // Debt tracking + invoicing (server-computed helpers included).
  assignedToId: string | null;
  assignedTo: { id: string; name: string } | null;
  writtenOffAt: string | null;
  writeOffNote: string | null;
  invoiceNumber: string | null;
  computedStatus: "PENDING" | "OVERDUE" | "PAID" | "WRITTEN_OFF";
  daysLate: number;
  paidLate: boolean;
  createdAt: string;
  updatedAt: string;
  product?: { id: string; title: string };
};

export type ExpenseDashboard = {
  summary: {
    totalPaid: number;
    totalPending: number;
    paymentsCount: number;
    pendingExpensesCount: number;
    pendingUsersCount: number;
  };
  payments: Array<{
    id: string;
    productId: string;
    productTitle: string;
    category: ExpenseCategory;
    customLabel: string | null;
    description: string;
    type: ExpenseType;
    amount: number;
    paidDate: string | null;
    paymentMethod: PaymentMethod | null;
    paidBy: { id: string; name: string } | null;
  }>;
  byPayer: Array<{ name: string; total: number; count: number }>;
  byCategory: Array<{ category: string; total: number; count: number }>;
  // Optional house filter ("all" when null).
  scope?: { productId: string | null; productTitle: string | null };
  houses?: Array<{ id: string; title: string }>;
  // Spend per member responsible for the bill, and spend per house.
  byAssignee?: Array<{ id: string; name: string; total: number; count: number }>;
  byHouseTotals?: Array<{ id: string; title: string; total: number; count: number }>;
  // Bills per category, paid or not (matches the "billed" totals).
  byCategoryBilled?: Array<{ category: string; total: number; count: number }>;
  // Occupancy: nights per house and per member (approved stays only).
  occupancy?: {
    totalNights: number;
    byHouse: Array<{ id: string; title: string; nights: number }>;
    byUser: Array<{ id: string; name: string; nights: number; spend: number }>;
  };
  pendingExpenses: Array<{
    id: string;
    productTitle: string;
    category: ExpenseCategory;
    description: string;
    amount: number;
    dueDate: string;
    type: ExpenseType;
  }>;
  familyPayers: Array<{ id: string; name: string; username: string }>;
};

export type NotificationChannel = {
  id: string;
  kind: "EMAIL" | "WHATSAPP";
  target: string;
  label: string | null;
  active: boolean;
  // CSV of subscribed NotificationTypes, or "ALL".
  types: string;
  createdAt: string;
};

export type NotificationRecord = {
  id: string;
  type: string;
  title: string;
  body: string;
  dispatch: Array<{ channel: string; target: string; ok: boolean; error?: string }>;
  createdAt: string;
};

export type PendingValidationUser = {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  createdAt: string;
};

